import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { switchMap, filter, take, catchError, tap, finalize } from 'rxjs/operators';

import { GestioneLoginService } from '../servizi/login/gestione-login.service';
import { UtilsService } from '../servizi/utils.service';
import { Router } from '@angular/router';

// Microsoft Azure AD SSO

// NOTE

// 1) Regola generale: in un Interceptor, evita subscribe() e lavora con il flusso di dati restituendo gli Observable.

//  Quando lavori con gli Interceptor di HttpClient, devi restituire un Observable invece di chiamare subscribe().

//  Se chiamassi subscribe(), faresti partire l'operazione, ma non restituiresti nulla al flusso di RxJS.
//  L'intercept di un HttpInterceptor deve restituire un Observable, perché Angular HttpClient si aspetta un flusso di dati.

// Non usiamo subscribe() perché gli Interceptor devono restituire un Observable.

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {

  // Per controllare se stiamo già aggiornando il token. Se il token è in fase di rinnovo, 
  // le altre richieste devono aspettare il risultato (evitando di incappare in un loop in caso di fallimento)
  private isRefreshing = false;

  // Per memorizzare il nuovo AT e restituirlo anche a chi si sottoscrive dopo la next
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  // ------------------------------------------------------------------------------

  constructor(
    private loginService: GestioneLoginService,
    private utilService: UtilsService,
    private router: Router
  ) {

  }

  // ------------------------------------------------------------------------------

  /**
   * intercept
   * 1) Fare solo una richiesta per rinnovare il token.
   * 2) Le altre richieste aspettano il nuovo token senza rifare la chiamata.
   * 3) Se il refresh fallisce, l'utente va rimandato alla login.
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // Prima dell' uscita...

    return next.handle(req).pipe( // Passa la richiesta HTTP al server
      catchError((error: HttpErrorResponse) => {
        if (error.status == 401) { // 401: Non autorizzato
          return this.handle401Error(req, next);
        }

        return throwError(() => error);
      })
    );

  }

  // ------------------------------------------------------------------------------

  /**
    *  A) Se il token è scaduto:
    *   - Avvia il refresh del token per ottenerne uno nuovo.
    *   - Se il refresh ha successo, ripete la richiesta originale con il nuovo token.
    *   - Se il refresh fallisce, chiama handleRefreshError()
    * 
    *  B) Se il refresh è già in corso:
    *   - Attende il nuovo token senza inviare un'altra richiesta di refresh.
   */
  private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Refresh non in corso
    if (!this.isRefreshing) {
      this.isRefreshing = true; // Per evitare richieste di refresh multiple
      this.refreshTokenSubject.next(null); // Pulisce refreshTokenSubject, segnalando che il token sta per essere aggiornato.

      return this.refreshToken().pipe(
        // Successo
        // Interrompe ogni richiesta precedente e ne genera una nuova con il token aggiornato.
        switchMap(tokenObj => {
          // - switchMap Prende il nuovo token restituito da refreshToken().
          // - Aggiorna la richiesta originale con il nuovo token usando this.addToken(req, token).
          // - next.handle() ripete la richiesta originale con il nuovo token.

          return next.handle(this.addToken(req, tokenObj.access_token));
        }),
        // Errore
        catchError(() => {
          this.handleRefreshError();
          return throwError(() => new Error('Session expired'));
        }),
        // Fine pipeline
        finalize(() => {
          // Indipendentemente dall'esito, alla fine della pipeline, imposta isRefreshing a 
          // false per consentire nuove richieste di refresh in futuro.
          this.isRefreshing = false;
        })
      );
    } else {
      // Se isRefreshing è già true, vuol dire che un'altra richiesta ha già avviato il processo di refresh.
      // Non effettua un'altra richiesta di refresh, ma si sottoscrive a refreshTokenSubject, che notificherà il nuovo token appena disponibile.

      // Quando il refreshTokenSubject emette un nuovo token, interrompe ogni richiesta precedente 
      // e ne genera una nuova con il token aggiornato.
      // Non usiamo subscribe() perché gli Interceptor devono restituire un Observable.
      return this.refreshTokenSubject.pipe( // Prende il flusso dei token rinnovati.
        filter(accessToken => accessToken !== null), // Aspetta che ci sia un nuovo token valido (ignora null).
        take(1), // Prende solo il primo valore emesso e poi si chiude.
        switchMap(accessToken => next.handle(this.addToken(req, accessToken))) // ripete la richiesta con il nuovo AT
      );
    }
  }
  
  // ------------------------------------------------------------------------------

  /**
   * refreshToken
   * ritorna un Observable, così possiamo aspettare il risultato.
   * 
   * Questa funzione tenta di ottenere un nuovo access token utilizzando il refresh token. 
   * Se l'operazione ha successo, aggiorna il token memorizzato nel servizio di login e notifica 
   * eventuali richieste in attesa. 
   * Se fallisce, esegue la gestione dell'errore.
   */
  private refreshToken(): Observable<any> {

    const refreshTokenAPIUrl = this.utilService.creaURL("auth", "token");
    const refreshToken = this.loginService.getRefresh();

    return this.utilService.postRequest(refreshTokenAPIUrl, { refresh_token: refreshToken }).pipe(
      // Per gestire il risultato senza modificarlo
      tap((valueObj: any) => { 
        if (valueObj && valueObj.access_token && valueObj.refresh_token) {
          this.loginService.setToken(valueObj.access_token);
          this.loginService.setRefresh(valueObj.refresh_token);

          // notifica eventuali richieste in attesa del token aggiornato.
          this.refreshTokenSubject.next(valueObj.access_token);
        } else {
          this.handleRefreshError();
        }
      }),
      catchError((error) => {
        console.error("Errore durante il refresh del token:", error);
        this.handleRefreshError();
        return throwError(() => new Error('Failed to refresh token'));
      })
    );
  }

  // ------------------------------------------------------------------------------

  private handleRefreshError() {

    console.log("---> handleRefreshError");
    // In caso di errore rimuovo i token dal Session Storage
    this.loginService.clearToken();
    // Redirect alla pagina di Login
    this.router.navigate(["/login"]);
  }

  // ------------------------------------------------------------------------------
  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
}